import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Bot, Plus, Loader2 } from 'lucide-react';
import Select from 'react-select';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import { TemporaryChatContext } from '../../Context/temporaryChatContext';
import { useAgents, useFeedaAgents, useTeams } from '../../hooks';
import sessionsService from '../../services/sessionsServices';
import Toast from '../Toast';
import AccentButton from '../ui/AccentButton';
import { sessionsInvalidateKey } from '../../hooks';

/** Plates Labs system default — never replace this as the chat landing default. */
export const DEFAULT_PEPPER_ID = '62d795b6-2df6-43ef-b5f5-7a3041f544de';
/** @deprecated use DEFAULT_PEPPER_ID */
export const DEFAULT_POLY_ID = DEFAULT_PEPPER_ID;

export const DEFAULT_PEPPER_AGENT = {
  label: 'Pepper',
  value: DEFAULT_PEPPER_ID,
  icon: 'https://feedalabs-agent-icon.s3.eu-north-1.amazonaws.com/pepper_66829e72a7ef4b93a7d81ea89ea7e1bd.jpg',
  mode: 'agent',
};
/** @deprecated use DEFAULT_PEPPER_AGENT */
export const DEFAULT_POLY_AGENT = DEFAULT_PEPPER_AGENT;

function toAgentOption(agent) {
  return {
    label: agent.agent_name,
    value: agent.id ?? agent.agent_id,
    icon: agent.icon,
    mode: 'agent',
  };
}

function toTeamOption(team) {
  return {
    label: team.team_name,
    value: team.team_id ?? team.id,
    icon: team.icon,
    mode: 'team',
  };
}

function isTeamSelection(agent) {
  return agent?.mode === 'team';
}

function findAgentById(agents, agentId) {
  if (!agents?.length || agentId == null || agentId === '') return null;
  return agents.find(a => String(a.id ?? a.agent_id) === String(agentId)) || null;
}

function findPepperAgent(agents) {
  if (!agents?.length) return null;
  return (
    agents.find(a => (a.agent_name || '').toLowerCase() === 'pepper') ||
    agents.find(a => String(a.id ?? a.agent_id) === DEFAULT_PEPPER_ID) ||
    null
  );
}

function isPepperSelection(agent) {
  if (!agent) return false;
  return String(agent.value) === DEFAULT_PEPPER_ID || (agent.label || '').toLowerCase() === 'pepper';
}

function flattenAgentOptions(groupedOptions) {
  return groupedOptions.flatMap(group => group.options || []);
}

const agentSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    minWidth: 180,
    backgroundColor: '#ffffff',
    borderColor: state.isFocused ? 'rgba(114, 27, 31, 0.5)' : '#e5e5e5',
    borderRadius: 8,
    boxShadow: 'none',
    fontSize: 14,
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    opacity: state.isDisabled ? 0.6 : 1,
    '&:hover': { borderColor: state.isFocused ? 'rgba(114, 27, 31, 0.5)' : '#d4d4d4' },
  }),
  valueContainer: base => ({ ...base, padding: '0 8px' }),
  input: base => ({ ...base, color: '#313131', margin: 0, padding: 0 }),
  singleValue: base => ({ ...base, color: '#313131', margin: 0 }),
  placeholder: base => ({ ...base, color: '#8d8d8d', margin: 0 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: base => ({
    ...base,
    color: '#8d8d8d',
    padding: '0 8px',
    '&:hover': { color: '#313131' },
  }),
  menu: base => ({
    ...base,
    backgroundColor: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: 50,
  }),
  menuList: base => ({ ...base, padding: 4, maxHeight: 280 }),
  group: base => ({ ...base, paddingTop: 4, paddingBottom: 4 }),
  groupHeading: base => ({
    ...base,
    color: '#8d8d8d',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    margin: 0,
    padding: '6px 10px 4px',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#f3f2ee' : state.isFocused ? '#f7f6f2' : 'transparent',
    color: '#313131',
    fontSize: 14,
    cursor: 'pointer',
    borderRadius: 6,
    padding: '8px 10px',
  }),
  noOptionsMessage: base => ({ ...base, color: '#8d8d8d', fontSize: 13 }),
};

function getAgentIconSrc(agent) {
  if (!agent?.icon) return null;
  if (agent.icon.startsWith('http://') || agent.icon.startsWith('https://')) {
    // Legacy SVG uploads are served by S3 as binary/octet-stream and won't render in <img>.
    return agent.icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : agent.icon;
  }
  if (agent.icon.startsWith('data:')) return agent.icon;
  return `data:image/png;base64,${agent.icon}`;
}

export default function PolyChatHeader() {
  const navigate = useNavigate();
  const { id: routeSessionId } = useParams();
  const queryClient = useQueryClient();
  const [switching, setSwitching] = useState(false);
  const { user } = useContextHook(AuthContext, v => ({
    user: v.user,
  }));
  const { sessionAgent, selectedAgent, updateAgent, setChatmessages, setSessionAgent, setSessionID } =
    useContext(TemporaryChatContext);

  const { data: agentsData, isLoading: agentsLoading, isFetched: agentsFetched } = useAgents();
  const {
    data: feedaData,
    isLoading: feedaLoading,
    isFetched: feedaFetched,
  } = useFeedaAgents();
  const { data: teamsData, isLoading: teamsLoading, isFetched: teamsFetched } = useTeams(user?.data?.ID);

  const userAgents = agentsData?.agents || [];
  const adminAgents = feedaData?.agents || [];
  const userTeams = teamsData?.teams || [];
  const allAgents = useMemo(() => [...adminAgents, ...userAgents], [adminAgents, userAgents]);
  const agentsReady = agentsFetched && feedaFetched;
  const agentsLoadingAny = agentsLoading || feedaLoading || teamsLoading;

  // Grouped dropdown: admin agents, user agents, then teams.
  const agentOptions = useMemo(() => {
    const poly =
      findPepperAgent(adminAgents) ||
      findAgentById(adminAgents, DEFAULT_PEPPER_ID) ||
      findPepperAgent(userAgents) ||
      findAgentById(userAgents, DEFAULT_PEPPER_ID);

    let adminOptions = adminAgents.map(toAgentOption);
    if (poly) {
      const polyOption = toAgentOption(poly);
      adminOptions = [polyOption, ...adminOptions.filter(option => String(option.value) !== String(polyOption.value))];
    }

    const adminIds = new Set(adminOptions.map(option => String(option.value)));
    const userOptions = userAgents
      .map(toAgentOption)
      .filter(option => !adminIds.has(String(option.value)));

    const teamOptions = userTeams.map(toTeamOption).filter(option => option.value != null && option.value !== '');

    const groups = [];
    if (adminOptions.length) {
      groups.push({ label: 'Admin created', options: adminOptions });
    }
    if (userOptions.length) {
      groups.push({ label: 'User created', options: userOptions });
    }
    if (teamOptions.length) {
      groups.push({ label: 'Teams', options: teamOptions });
    }
    return groups;
  }, [adminAgents, userAgents, userTeams]);

  const flatAgentOptions = useMemo(() => flattenAgentOptions(agentOptions), [agentOptions]);

  // System default is always Pepper: hydrate Pepper from API on fresh chat (no session).
  // On an existing session, only refresh the current agent’s icon/label from API.
  // Never overwrite an active team chat selection.
  useEffect(() => {
    if (!agentsReady || !allAgents.length) return;
    if (isTeamSelection(selectedAgent) || isTeamSelection(sessionAgent)) return;

    const currentId = selectedAgent?.value || sessionAgent?.value;
    const freshChat = !routeSessionId;
    const shouldDefaultToPepper = freshChat && (isPepperSelection(selectedAgent) || isPepperSelection(sessionAgent) || !currentId);

    const fromApi = shouldDefaultToPepper
      ? findPepperAgent(allAgents) || findAgentById(allAgents, DEFAULT_PEPPER_ID) || findAgentById(allAgents, currentId)
      : findAgentById(allAgents, currentId);

    if (!fromApi) return;

    const agentObj = toAgentOption(fromApi);
    const selectedNeedsUpdate =
      String(selectedAgent?.value) !== String(agentObj.value) ||
      selectedAgent?.icon !== agentObj.icon ||
      selectedAgent?.label !== agentObj.label;

    if (selectedNeedsUpdate && (shouldDefaultToPepper || String(selectedAgent?.value) === String(agentObj.value))) {
      updateAgent(agentObj);
    }

    const sessionMatchesCurrent =
      !routeSessionId ||
      String(sessionAgent?.value) === String(currentId) ||
      isPepperSelection(sessionAgent);

    if (
      sessionMatchesCurrent &&
      (String(sessionAgent?.value) !== String(agentObj.value) ||
        sessionAgent?.icon !== agentObj.icon ||
        sessionAgent?.label !== agentObj.label)
    ) {
      // On fresh chat always sync session agent to Pepper (system default).
      if (shouldDefaultToPepper || String(sessionAgent?.value) === String(agentObj.value) || isPepperSelection(sessionAgent)) {
        setSessionAgent(agentObj);
      }
    }
  }, [
    allAgents,
    agentsReady,
    routeSessionId,
    selectedAgent,
    sessionAgent,
    updateAgent,
    setSessionAgent,
  ]);

  // Keep team label/icon fresh from teams list when chatting with a team.
  useEffect(() => {
    if (!teamsFetched || !userTeams.length) return;
    const current = isTeamSelection(selectedAgent) ? selectedAgent : isTeamSelection(sessionAgent) ? sessionAgent : null;
    if (!current?.value) return;

    const fromApi = userTeams.find(t => String(t.team_id ?? t.id) === String(current.value));
    if (!fromApi) return;

    const teamObj = toTeamOption(fromApi);
    if (
      String(selectedAgent?.value) === String(teamObj.value) &&
      (selectedAgent?.label !== teamObj.label || selectedAgent?.icon !== teamObj.icon || selectedAgent?.mode !== 'team')
    ) {
      updateAgent(teamObj);
    }
    if (
      String(sessionAgent?.value) === String(teamObj.value) &&
      (sessionAgent?.label !== teamObj.label || sessionAgent?.icon !== teamObj.icon || sessionAgent?.mode !== 'team')
    ) {
      setSessionAgent(teamObj);
    }
  }, [teamsFetched, userTeams, selectedAgent, sessionAgent, updateAgent, setSessionAgent]);

  const selectedOption = useMemo(() => {
    const current = isTeamSelection(selectedAgent)
      ? selectedAgent
      : isTeamSelection(sessionAgent)
        ? sessionAgent
        : routeSessionId && sessionAgent?.value
          ? // On an existing session the agent from its history wins over any leftover selection.
            sessionAgent
          : selectedAgent?.value
            ? selectedAgent
            : sessionAgent;
    const currentValue = current?.value;
    if (currentValue == null || currentValue === '') return null;

    const fromList = flatAgentOptions.find(
      option => String(option.value) === String(currentValue) && (option.mode || 'agent') === (current?.mode || 'agent'),
    );
    if (fromList) return fromList;

    // Agent or team outside the current lists (Discover team, session agent not owned by
    // this user) — still show it as the value instead of falling back to the placeholder.
    if (current?.label) {
      return {
        label: current.label,
        value: current.value,
        icon: current.icon,
        mode: current.mode || 'agent',
      };
    }
    return null;
  }, [flatAgentOptions, selectedAgent, sessionAgent, routeSessionId]);

  // Prefer team selection, then live API option data, then context.
  const displayAgent = useMemo(() => {
    if (isTeamSelection(selectedAgent)) return selectedAgent;
    if (isTeamSelection(sessionAgent)) return sessionAgent;
    if (selectedOption) {
      return {
        label: selectedOption.label,
        value: selectedOption.value,
        icon: selectedOption.icon,
        mode: selectedOption.mode || 'agent',
      };
    }
    return selectedAgent?.label ? selectedAgent : sessionAgent;
  }, [selectedOption, sessionAgent, selectedAgent]);

  const iconSrc = useMemo(() => getAgentIconSrc(displayAgent), [displayAgent]);
  const isTeamChat = isTeamSelection(displayAgent);
  const currentAgentId = selectedAgent?.value || sessionAgent?.value;
  const apiAgent =
    findAgentById(allAgents, currentAgentId) ||
    ((isPepperSelection(selectedAgent) || isPepperSelection(sessionAgent)) && findPepperAgent(allAgents));
  const waitingForAgentData =
    !isTeamChat && (!agentsReady || (!!apiAgent && !iconSrc));

  const startSessionWith = async agentObj => {
    setSwitching(true);
    try {
      setChatmessages([]);
      updateAgent(agentObj);
      setSessionAgent(agentObj);
      const sessionRes = await sessionsService.createSession({
        user_id: user?.data?.ID,
        platform_id: 'plateslabs',
        agent_id: agentObj?.value,
        project_id: 'test',
        mode: agentObj?.mode,
      });
      setSessionID(sessionRes?.session_id);
      queryClient.invalidateQueries({ queryKey: sessionsInvalidateKey });
      navigate(`/chat/${sessionRes?.session_id}`);
    } catch (error) {
      console.log(error);
      Toast({ type: 'error', message: 'Could not start a chat with this agent' });
    } finally {
      setSwitching(false);
    }
  };

  const handleNewChat = () => {
    const agent = selectedAgent?.value ? selectedAgent : sessionAgent;
    if (!agent?.value) {
      Toast({ type: 'info', message: 'Please select an agent first' });
      return;
    }
    startSessionWith(agent);
  };

  const handleAgentChange = option => {
    if (!option) return;
    // Selecting an agent only updates the active agent — detach any existing session so
    // chat cannot reuse an old session_id until the user creates a new one.
    const agentObj = {
      label: option.label,
      value: option.value,
      icon: option.icon,
      mode: option.mode || 'agent',
    };
    updateAgent(agentObj);
    setSessionAgent(agentObj);
    setChatmessages([]);
    setSessionID(null);
    navigate('/chat');
  };

  const accentColor = '#721b1f';

  return (
    <div className="poly-chat-screen-header px-6 py-3 border-b border-shell-border flex items-center gap-3 flex-shrink-0 bg-shell-main">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
        style={{ backgroundColor: `${accentColor}1A` }}>
        {waitingForAgentData ? (
          <div className="w-full h-full animate-pulse bg-shell-card" aria-hidden="true" />
        ) : iconSrc ? (
          <img src={iconSrc} alt="" className="w-full h-full object-cover" />
        ) : (
          <Bot className="w-4 h-4" style={{ color: accentColor }} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        {waitingForAgentData ? (
          <div className="space-y-1.5" aria-hidden="true">
            <div className="h-3.5 w-20 rounded bg-shell-card animate-pulse" />
            <div className="h-3 w-16 rounded bg-shell-card animate-pulse" />
          </div>
        ) : (
          <>
            <p className="text-sm font-semibold text-[var(--text-color)] truncate">
              {displayAgent?.label || 'Pepper'}
            </p>
            <p className="text-xs text-muted">{isTeamChat ? 'Team Chat' : 'Pepper Chat'}</p>
          </>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <AccentButton
          type="button"
          onClick={handleNewChat}
          disabled={switching || waitingForAgentData}
          className="px-3 py-1.5 text-sm">
          {switching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          New Chat
        </AccentButton>
        {agentsLoadingAny && !agentsReady && !isTeamChat ? (
          <div
            className="h-[34px] min-w-[180px] rounded-lg border border-shell-card-border bg-shell-main animate-pulse"
            aria-hidden="true"
          />
        ) : (
          <Select
            options={agentOptions}
            value={selectedOption}
            onChange={handleAgentChange}
            isDisabled={switching}
            isSearchable
            placeholder={isTeamChat ? 'Team selected' : 'Assign agent'}
            noOptionsMessage={() => 'No agents or teams found'}
            styles={agentSelectStyles}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            classNamePrefix="agent-select"
          />
        )}
      </div>
    </div>
  );
}
